<template>
  <div
    class="result-card"
    data-testid="workspace-document-search-result-card"
    :class="{
      'result-card--selected': selected,
      'result-card--disabled': isDisabled,
    }"
    @click="handleCardClick"
  >
    <div class="result-card__preview">
      <img
        v-if="thumbnailUrl"
        class="result-card__thumbnail"
        :src="thumbnailUrl"
        :alt="data.title || data._id"
        loading="lazy"
      />
      <font-awesome-icon
        v-else
        :icon="fileIcon"
        class="result-card__fallback-icon"
      />
    </div>
    <div class="result-card__content">
      <div class="result-card__header">
        <h4 :title="data.title || data._id">{{ data.title || data._id }}</h4>
        <div class="result-card__header-right">
          <span v-if="fileExtensionLabel" class="result-card__ext-badge">
            {{ fileExtensionLabel }}
          </span>
          <div
            v-if="isDisabled"
            v-tooltip.top="statusIndicatorTooltip"
            class="result-card__status-icon"
            @click.stop
          >
            <font-awesome-icon
              :icon="statusIndicatorIcon"
              :class="statusIndicatorClass"
            />
          </div>
          <div v-else class="result-card__checkbox" @click.stop>
            <PVCheckbox
              :model-value="selected"
              :binary="true"
              @update:model-value="handleCheckboxUpdate"
            />
          </div>
        </div>
      </div>
      <div class="result-card__meta">
        <div class="result-card__meta-row">
          <span class="result-card__meta-label">
            {{ t('workspaceDocuments.picker.stationNumber') }}:
          </span>
          <span class="result-card__meta-value">
            {{ data.stationNumber || notPresentLabel }}
          </span>
        </div>
        <div class="result-card__meta-row">
          <span class="result-card__meta-label">
            {{ t('workspaceDocuments.picker.kks') }}:
          </span>
          <span class="result-card__meta-value">
            {{ data.kks.length ? data.kks.join(', ') : notPresentLabel }}
          </span>
        </div>
        <div class="result-card__meta-row">
          <span class="result-card__meta-label">
            {{ t('workspaceDocuments.picker.dcc') }}:
          </span>
          <span class="result-card__meta-value">
            {{
              data.masterDocumentClassificationCode?.label || notPresentLabel
            }}
          </span>
        </div>
        <div class="result-card__meta-row">
          <span class="result-card__meta-label">
            {{ t('workspaceDocuments.picker.folderNumber') }}:
          </span>
          <span class="result-card__meta-value">
            {{ data.folderNumber || notPresentLabel }}
          </span>
        </div>
        <div class="result-card__meta-row">
          <span class="result-card__meta-label">
            {{ t('workspaceDocuments.picker.documentNumber') }}:
          </span>
          <span class="result-card__meta-value">
            {{ data.creatorDocumentNumber || notPresentLabel }}
          </span>
        </div>
        <div class="result-card__meta-row">
          <span class="result-card__meta-label">
            {{ t('workspaceDocuments.picker.lastModifiedBy') }}:
          </span>
          <span class="result-card__meta-value">
            {{
              data.lastModifiedBy?.displayName ||
              data.createdBy?.displayName ||
              notPresentLabel
            }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import {
    faCircleCheck,
    faCircleQuestion,
    faEnvelope,
    faFileCad,
    faFileCircleQuestion,
    faFileExcel,
    faFileImage,
    faFilePdf,
    faFilePowerpoint,
    faFileWord,
    faFileXml,
    faFileZip,
  } from '@fortawesome/pro-regular-svg-icons';
  import {
    DEFAULT_TRANSFERED_DOC_STATUS,
    IWorkspaceDocumentSearchResultItemDto,
    WorkspaceDocumentPickerMode,
  } from 'types';
  import { useI18n } from 'vue-i18n';
  import PVCheckbox from 'primevue/checkbox';

  type DisabledReason =
    | 'already-added-editable-current-workspace'
    | 'already-added-editable-other-workspace'
    | 'invalid-editable-status'
    | 'already-added-readonly'
    | null;

  const props = defineProps<{
    data: IWorkspaceDocumentSearchResultItemDto;
    mode: WorkspaceDocumentPickerMode;
    selected: boolean;
    workspaceId: string;
  }>();

  const emit = defineEmits<{
    (e: 'toggle', value: IWorkspaceDocumentSearchResultItemDto): void;
  }>();

  const { t } = useI18n();
  const notPresentLabel = computed(() =>
    t('workspaceDocuments.picker.notPresent'),
  );
  const thumbnailUrl = computed(
    () =>
      props.data.thumbnail?.large?.url ||
      props.data.thumbnail?.medium?.url ||
      props.data.thumbnail?.small?.url,
  );
  const fileExtensionLabel = computed(() =>
    props.data.fileExtension ? props.data.fileExtension.toLowerCase() : '',
  );
  const fileIcon = computed(() => {
    const extension = props.data.fileExtension?.toLowerCase();
    if (!extension) {
      return faFileCircleQuestion;
    }
    if (['doc', 'docx', 'docm'].includes(extension)) {
      return faFileWord;
    }
    if (extension === 'pdf') {
      return faFilePdf;
    }
    if (['xls', 'xlsx'].includes(extension)) {
      return faFileExcel;
    }
    if (['dwg', 'tif', 'cad', 'dxf', 'dgn'].includes(extension)) {
      return faFileCad;
    }
    if (['ppt', 'pptx'].includes(extension)) {
      return faFilePowerpoint;
    }
    if (['msg', 'eml'].includes(extension)) {
      return faEnvelope;
    }
    if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
      return faFileImage;
    }
    if (['zip', '7z'].includes(extension)) {
      return faFileZip;
    }
    if (extension === 'xml') {
      return faFileXml;
    }
    return faFileCircleQuestion;
  });
  const disabledReason = computed<DisabledReason>(() => {
    if (props.mode === WorkspaceDocumentPickerMode.EDIT) {
      if (props.data.copiedForWorkspace === props.workspaceId) {
        return 'already-added-editable-current-workspace';
      }
      if (
        props.data.copiedForWorkspace &&
        props.data.copiedForWorkspace !== props.workspaceId
      ) {
        return 'already-added-editable-other-workspace';
      }
      if (!props.data.canAddToWorkspaceAsEditable) {
        return 'invalid-editable-status';
      }
      return null;
    }

    if (props.data.isAddedToWorkspaceAsReadonly) {
      return 'already-added-readonly';
    }

    return null;
  });
  const isDisabled = computed(() => disabledReason.value !== null);
  const statusIndicatorIcon = computed(() => {
    if (
      disabledReason.value === 'already-added-editable-current-workspace' ||
      disabledReason.value === 'already-added-readonly'
    ) {
      return faCircleCheck;
    }

    return faCircleQuestion;
  });
  const statusIndicatorClass = computed(() => ({
    'result-card__status-icon-symbol': true,
    'result-card__status-icon-symbol--success':
      disabledReason.value === 'already-added-editable-current-workspace' ||
      disabledReason.value === 'already-added-readonly',
    'result-card__status-icon-symbol--help':
      disabledReason.value === 'already-added-editable-other-workspace' ||
      disabledReason.value === 'invalid-editable-status',
  }));
  const statusIndicatorTooltip = computed(() => {
    switch (disabledReason.value) {
      case 'already-added-editable-current-workspace':
        return t(
          'workspaceDocuments.picker.disabledReasons.alreadyAddedToWorkspaceEditable',
        );
      case 'already-added-editable-other-workspace':
        return t(
          'workspaceDocuments.picker.disabledReasons.alreadyAddedToOtherWorkspaceEditable',
        );
      case 'invalid-editable-status':
        return t(
          'workspaceDocuments.picker.disabledReasons.invalidEditableStatus',
          { statusLabel: DEFAULT_TRANSFERED_DOC_STATUS },
        );
      case 'already-added-readonly':
        return t(
          'workspaceDocuments.picker.disabledReasons.alreadyAddedToWorkspaceReadOnly',
        );
      default:
        return '';
    }
  });

  function handleCardClick(): void {
    if (isDisabled.value) return;
    emit('toggle', props.data);
  }

  function handleCheckboxUpdate(): void {
    emit('toggle', props.data);
  }
</script>

<style scoped lang="scss">
  .result-card {
    border-radius: 0.75rem;
    border: 1px solid var(--p-surface-200);
    padding: 1rem 1.1rem;
    cursor: pointer;
    display: grid;
    grid-template-columns: 6rem minmax(0, 1fr);
    column-gap: 1rem;
    background: var(--p-surface-50);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      border-color: var(--p-surface-300);
    }

    &--disabled {
      cursor: default;
      opacity: 0.82;

      &:hover {
        border-color: var(--p-surface-200);
      }
    }

    &--selected {
      border-color: var(--p-primary-500);
      box-shadow: 0 0 0 1px
        color-mix(in srgb, var(--p-primary-500) 35%, transparent);
    }

    &__preview {
      align-self: center;
      width: 6rem;
      height: 6rem;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }

    &__thumbnail {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }

    &__fallback-icon {
      font-size: 3.2rem;
      color: #26354d;
    }

    &__content {
      min-width: 0;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      column-gap: 1rem;
      margin-bottom: 0.5rem;

      h4 {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 700;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    &__header-right {
      display: flex;
      align-items: center;
      column-gap: 0.6rem;
      flex-shrink: 0;
    }

    &__ext-badge {
      display: inline-flex;
      align-items: center;
      height: 1.25rem;
      padding: 0 0.5rem;
      border-radius: 0.4rem;
      background: var(--p-surface-200);
      color: #5f6b7d;
      font-size: 0.76rem;
      font-weight: 700;
      text-transform: lowercase;
    }

    &__checkbox {
      display: flex;
    }

    &__status-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.25rem;
      height: 1.25rem;
      cursor: help;
    }

    &__status-icon-symbol {
      font-size: 1rem;

      &--success {
        color: $success-color;
      }

      &--help {
        color: $warning-color;
      }
    }

    &__meta {
      display: flex;
      flex-direction: column;
      row-gap: 0.2rem;
      font-size: 0.95rem;
    }

    &__meta-row {
      display: flex;
      justify-content: space-between;
      column-gap: 1rem;
      align-items: start;
    }

    &__meta-label {
      color: #495057;
      white-space: nowrap;
    }

    &__meta-value {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #25364f;
      text-align: right;
    }
  }
</style>
